import React, { useMemo, useState } from 'react';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import { exportBandwidthActivityTypeCountTimeExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthActivityTypeCountTimePdf } from './bandwidthDashboardPdfExport';
import type { BandwidthActivityTypeCountTimePoint } from './bandwidthDashboardTypes';
import {
  bwKpiPanelSubtitleClassShrink0,
  bwKpiTableBodyFirstColClassWithBg,
  bwKpiTableFooterRowClass,
  bwKpiTableHeaderCellClass,
  bwKpiTableHeaderClass,
  bwKpiTableHeaderFirstColClass,
  bwKpiTableZebraEven,
  bwKpiTableZebraOdd,
  bwProgressFillAccentClass,
  bwProgressFillSteelClass,
  bwProgressTrackClass,
} from './bandwidthKpiTableChrome';
import { fmtBwAvg, fmtBwCount, fmtBwDecimal } from './bandwidthFormat';

export interface BandwidthActivityTypeCountTimePanelProps {
  title: string;
  subtitle?: string;
  rows: BandwidthActivityTypeCountTimePoint[];
  emptyHint?: string;
  exportDateFrom: string;
  exportDateTo: string;
}

type SortKey = 'label' | 'count' | 'hours' | 'avgHrs';
type SortDir = 'asc' | 'desc';

function sortIndicator(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'desc' ? '↓' : '↑';
}

function ariaSortForColumn(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

const BandwidthActivityTypeCountTimePanel: React.FC<BandwidthActivityTypeCountTimePanelProps> = ({
  title,
  subtitle,
  rows,
  emptyHint,
  exportDateFrom,
  exportDateTo,
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('hours');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const filtered = useMemo(
    () => rows.filter((r) => r.totalActivityCount > 0 || r.timeSpentHours > 0),
    [rows]
  );

  const { sorted, maxCount, maxHours, totals } = useMemo(() => {
    const enriched = filtered.map((r) => ({
      ...r,
      avgHrsOnActivity:
        r.totalActivityCount > 0 ? r.timeSpentHours / r.totalActivityCount : (null as number | null),
    }));

    const dirMul = sortDir === 'desc' ? -1 : 1;
    const sortedInner = [...enriched].sort((a, b) => {
      if (sortKey === 'label') {
        const cmp = a.activityTypeLabel.localeCompare(b.activityTypeLabel, undefined, { sensitivity: 'base' });
        return dirMul * cmp;
      }
      if (sortKey === 'count') return dirMul * (a.totalActivityCount - b.totalActivityCount);
      if (sortKey === 'hours') return dirMul * (a.timeSpentHours - b.timeSpentHours);
      const avA = a.avgHrsOnActivity ?? -1;
      const avB = b.avgHrsOnActivity ?? -1;
      return dirMul * (avA - avB);
    });

    const maxCountInner = sortedInner.length ? Math.max(...sortedInner.map((r) => r.totalActivityCount)) : 0;
    const maxHoursInner = sortedInner.length ? Math.max(...sortedInner.map((r) => r.timeSpentHours)) : 0;

    const totalCount = sortedInner.reduce((s, r) => s + r.totalActivityCount, 0);
    const totalHours = sortedInner.reduce((s, r) => s + r.timeSpentHours, 0);
    const totalAvg = totalCount > 0 ? totalHours / totalCount : null;

    return {
      sorted: sortedInner,
      maxCount: maxCountInner,
      maxHours: maxHoursInner,
      totals: { totalCount, totalHours, totalAvg },
    };
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  };

  const exportRowsForFile = useMemo(
    () =>
      sorted.map((r) => ({
        activityType: r.activityType,
        activityTypeLabel: r.activityTypeLabel,
        totalActivityCount: r.totalActivityCount,
        timeSpentHours: r.timeSpentHours,
      })),
    [sorted]
  );

  const exportTitleEnd = (
    <BandwidthPanelExportShell
      disabled={exportRowsForFile.length === 0}
      ariaLabel={`Export ${title}`}
      buildPdf={() =>
        exportBandwidthActivityTypeCountTimePdf(
          exportRowsForFile,
          `Bandwidth dashboard — ${title}`,
          exportDateFrom,
          exportDateTo
        )
      }
      buildExcel={() => exportBandwidthActivityTypeCountTimeExcel(exportRowsForFile, exportDateFrom, exportDateTo)}
    />
  );

  if (filtered.length === 0) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[min(36rem,78vh)] lg:max-h-[520px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? <p className="mb-2 text-xs text-slate-500">{subtitle}</p> : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={exportTitleEnd}
      className="h-full min-h-0 max-h-[min(36rem,78vh)] lg:max-h-[520px]"
      flexFill
      bodyClassName="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0"
    >
      {subtitle ? (
        <p className={bwKpiPanelSubtitleClassShrink0}>{subtitle}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto px-0">
        <table className="w-full min-w-[320px] border-collapse text-left text-xs">
          <thead>
            <tr className={bwKpiTableHeaderClass}>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'label', sortDir)}
                className={`min-w-[140px] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass} ${bwKpiTableHeaderFirstColClass}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort('label')}
                  className="inline-flex items-center gap-1 rounded px-0.5 text-left font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                >
                  Activity type
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'label', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'count', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort('count')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                >
                  Activity count
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'count', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'hours', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort('hours')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                >
                  Time (hrs)
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'hours', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'avgHrs', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSort('avgHrs')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                >
                  Avg hrs / activity
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'avgHrs', sortDir)}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="text-slate-800">
            {sorted.map((r, i) => {
              const stripe = i % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
              const countPct = maxCount > 0 ? Math.min(100, (r.totalActivityCount / maxCount) * 100) : 0;
              const hoursPct = maxHours > 0 ? Math.min(100, (r.timeSpentHours / maxHours) * 100) : 0;
              return (
                <tr key={r.activityType} className={stripe}>
                  <td
                    className={`max-w-[min(28rem,40vw)] px-2 py-2 align-top font-medium leading-snug text-slate-900 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: i })}`}
                  >
                    {r.activityTypeLabel}
                  </td>
                  <td className={`px-3 py-2 align-top ${stripe}`}>
                    <div className="text-right tabular-nums text-slate-900">{fmtBwCount(r.totalActivityCount)}</div>
                    <div className={bwProgressTrackClass}>
                      <div
                        className={bwProgressFillSteelClass}
                        style={{ width: `${countPct}%` }}
                        title={`${fmtBwCount(r.totalActivityCount)} activities`}
                      />
                    </div>
                  </td>
                  <td className={`px-3 py-2 align-top ${stripe}`}>
                    <div className="text-right tabular-nums text-slate-900">{fmtBwDecimal(r.timeSpentHours)}</div>
                    <div className={bwProgressTrackClass}>
                      <div
                        className={bwProgressFillAccentClass}
                        style={{ width: `${hoursPct}%` }}
                        title={`${fmtBwDecimal(r.timeSpentHours)} hours`}
                      />
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums text-slate-800 ${stripe}`}>
                    {fmtBwAvg(r.avgHrsOnActivity)}
                  </td>
                </tr>
              );
            })}
            <tr className={bwKpiTableFooterRowClass}>
              <td className={`px-3 py-2.5 ${bwKpiTableBodyFirstColClassWithBg({ footer: true })}`}>Total</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwCount(totals.totalCount)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwDecimal(totals.totalHours)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtBwAvg(totals.totalAvg)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthActivityTypeCountTimePanel;
